/**
 * The 24 theme packs, one per level, in level order. Theme ids come from
 * LEVEL_THEMES in levels/levels.ts; test/themes.test.ts checks that the two
 * lists agree and that every palette meets the contrast and brightness rules.
 *
 * The packs themselves live in ./packs (six per file, grouped by grade band).
 * docs/THEMES.md explains the data shape and how to add or change a theme.
 */
import { LEVEL_COUNT } from '../config.ts';
import type { ThemePack } from './types.ts';
import { LEVELS_01_06 } from './packs/levels01-06.ts';
import { LEVELS_07_12 } from './packs/levels07-12.ts';
import { LEVELS_13_18 } from './packs/levels13-18.ts';
import { LEVELS_19_24 } from './packs/levels19-24.ts';

// Colour maths used by the theme tests and the web background painters.
// Re-exported here because the package entry point exposes this module.
export {
  compositeColor,
  contrastRatio,
  cssRgba,
  formatHexColor,
  isHexColor,
  mixHexColors,
  parseHexColor,
  relativeLuminance,
  shadeHexColor,
} from './color.ts';
export type { ColorInput, Rgba } from './color.ts';

/** 24 theme packs, one per level (ids in levels/levels.ts LEVEL_THEMES). */
export const THEMES: ThemePack[] = [...LEVELS_01_06, ...LEVELS_07_12, ...LEVELS_13_18, ...LEVELS_19_24];

export function getTheme(id: string): ThemePack {
  const t = THEMES.find((x) => x.id === id);
  if (!t) throw new Error(`unknown theme ${id}`);
  return t;
}

/** Theme unlocked at a level (1–24; out-of-range levels are clamped). */
export function getThemeForLevel(level: number): ThemePack {
  const index = Math.min(LEVEL_COUNT, Math.max(1, Math.round(level))) - 1;
  const t = THEMES[index];
  if (!t) throw new Error(`no theme for level ${level}`);
  return t;
}
